/**
 * A short performance: two to four slides, on the same stage the first
 * meeting and the Sunday report use.
 *
 * Everything structural is borrowed rather than rebuilt — the swipeable
 * stage, its slide vocabulary, its aurora, its safe-area handling. What is
 * different is the scale. The meeting is eleven slides and unskippable; this
 * is three and closable, because a hundred-day streak deserves a room of its
 * own and does not deserve to be a thing you have to sit through on the way
 * to logging breakfast.
 *
 * The first meeting is untouched by all of this and passes no `cues`, so it
 * behaves exactly as it always has.
 */

import { useBackLayer } from '@gramas/ui'
import { useState } from 'react'
import { Aside, Beat, Hero, Kicker, Line, Mark } from '../stage/parts.js'
import { Stage } from '../stage/Stage.js'
import type { CeremonyCopy } from './copy.js'

/** One hue per beat, walking from her violet toward the app's own lime. */
const HUES = ['#8b74ff', '#7b6ae8', '#6f7fd0', '#84a25a']

export interface MiniCeremonyProps {
  copy: CeremonyCopy
  onClose: () => void
}

export function MiniCeremony({ copy, onClose }: MiniCeremonyProps) {
  const [index, setIndex] = useState(0)
  const slide = copy.slides[Math.min(index, copy.slides.length - 1)]!

  useBackLayer(true, onClose)

  return (
    <Stage
      label={copy.label}
      count={copy.slides.length}
      index={index}
      onIndex={setIndex}
      onFinish={onClose}
      finishLabel={copy.finishLabel}
      onClose={onClose}
      crest="Glitimė"
      note={copy.note}
      hue={HUES[Math.min(index, HUES.length - 1)]!}
      cues
    >
      <Beat
        // Her mark opens the first slide and closes the last, the same way it
        // bookends both of the long performances. In between, the words are
        // the picture.
        top={index === 0 || index === copy.slides.length - 1 ? <Mark /> : undefined}
      >
        {slide.kicker != null && <Kicker>{slide.kicker}</Kicker>}
        <Hero delayMs={slide.kicker == null ? 120 : 320}>{slide.hero}</Hero>
        {slide.body != null && <Line delayMs={slide.kicker == null ? 480 : 680}>{slide.body}</Line>}
        {slide.aside != null && <Aside delayMs={900}>{slide.aside}</Aside>}
      </Beat>
    </Stage>
  )
}
